import useProtoMethod from "./useProtoMethod";
import { SearchRequest, SearchResponse } from "protogen/search_service_pb";
import { getInferenceDomain } from "../constants";
import { PlainMessage } from "@bufbuild/protobuf";

const SEARCH_CACHE_TTL_MS = 2 * 60 * 1000;
const SEARCH_CACHE_MAX_ENTRIES = 25;

type SearchCacheEntry = {
  createdMs: number;
  response: SearchResponse;
};

const searchCache = new Map<string, SearchCacheEntry>();

const searchCacheKey = (
  userRef: string,
  request: PlainMessage<SearchRequest>,
) =>
  JSON.stringify({
    userRef,
    queryText: request.queryText,
    adminSearchMode: request.adminSearchMode,
    strictlyChronological: request.strictlyChronological,
    maxResults: request.maxResults,
    filters: [...(request.filters || [])]
      .map((filter) => ({
        field: filter.field,
        operator: filter.operator,
        value: filter.value,
      }))
      .sort((a, b) =>
        `${a.field}:${a.operator}:${a.value}`.localeCompare(
          `${b.field}:${b.operator}:${b.value}`,
        ),
      ),
  });

const getCachedSearch = (
  userRef: string,
  request: PlainMessage<SearchRequest>,
): SearchResponse | null => {
  const key = searchCacheKey(userRef, request);
  const cached = searchCache.get(key);
  if (!cached) return null;
  if (Date.now() - cached.createdMs > SEARCH_CACHE_TTL_MS) {
    searchCache.delete(key);
    return null;
  }
  return cached.response;
};

const setCachedSearch = (
  userRef: string,
  request: PlainMessage<SearchRequest>,
  response: SearchResponse,
) => {
  const key = searchCacheKey(userRef, request);
  searchCache.delete(key);
  searchCache.set(key, { createdMs: Date.now(), response });
  while (searchCache.size > SEARCH_CACHE_MAX_ENTRIES) {
    const oldestKey = searchCache.keys().next().value;
    if (!oldestKey) break;
    searchCache.delete(oldestKey);
  }
};

const useSearch = (callback?: (r: SearchResponse) => void) => {
  return useProtoMethod<SearchRequest, SearchResponse>(
    "Search",
    SearchResponse,
    {
      baseUrl: getInferenceDomain(),
      callback,
    },
  );
};

export { getCachedSearch, setCachedSearch, useSearch };
